/**
 * AI Orchestrator (Advisory P6) — app-open scheduled scans + notifications.
 *
 * ARCHITECTURE FACT (why "app-open"): scans run client-side with the user's
 * own API key. There is no server to run them (Netlify growth is vetoed), so
 * the orchestrator fires when the app is open and a slot has passed, with a
 * single catch-up scan on load for the latest missed slot of the day.
 *
 * Slots (Türkiye time): 08:30 weekdays (pre-market) · 12:30 weekdays
 * (intraday) · 18:00 daily (crypto/US never sleep).
 *
 * After an auto-scan, HIGH-urgency recommendations notify via swNotify
 * (per-slot tag = no duplicate notifications; user toggles still apply).
 */

import { S, save } from '../core/state.js';
import { isAIConfigured } from '../core/ai.js';
import { runCopilotScan, isCopilotScanning, getCopilotRecommendations } from './ai-copilot.js';
import { swNotify } from './push-notifications.js';

const CHECK_EVERY_MS = 5 * 60 * 1000;
const FRESHNESS_MS = 4 * 60 * 60 * 1000; // beyond this, data is stale → auto-refresh
/** [hour, minute, weekdaysOnly] in TR local time */
const SLOTS = [[8, 30, true], [12, 30, true], [18, 0, false]];

let _timer = null;

/** Latest slot already passed today (TR time), or null. Exported for tests. */
export function _latestPassedSlot(now = new Date()) {
  // Render in Europe/Istanbul regardless of machine tz
  const tr = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
  const dow = tr.getDay(); // 0 Sun .. 6 Sat
  const weekday = dow >= 1 && dow <= 5;
  let latest = null;
  for (const [h, m, wOnly] of SLOTS) {
    if (wOnly && !weekday) continue;
    if (tr.getHours() > h || (tr.getHours() === h && tr.getMinutes() >= m)) {
      latest = `${tr.getFullYear()}-${String(tr.getMonth() + 1).padStart(2, '0')}-${String(tr.getDate()).padStart(2, '0')}#${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
    }
  }
  return latest;
}

/** Run one auto-scan for the given slot key, then notify HIGH-urgency recs. */
async function _autoScan(slotKey) {
  if (!isAIConfigured() || isCopilotScanning()) return;
  if (!S.aiCopilot) S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
  S.aiCopilot.lastAutoScan = { slot: slotKey, ts: new Date().toISOString() };
  save(); // claim the slot first — a failed scan should not retry every 5 min
  console.warn('[ORCH] Auto-scan for slot', slotKey);
  const res = await runCopilotScan({ force: true });
  if (!res || !res.ok) {
    // failure reason must be inspectable, not silent (audit W1-4)
    S.aiCopilot.lastAutoScan.error = (res && res.error) || 'unknown failure';
    save();
    return;
  }
  const high = getCopilotRecommendations().filter(r => r.urgency === 'high').slice(0, 3);
  if (high.length) {
    swNotify('portfolio',
      `AI Advisory — ${high.length} high-urgency move${high.length > 1 ? 's' : ''}`,
      high.map(r => r.title).join(' · '),
      { tag: 'advisory-' + slotKey, data: { page: 'advisory' } });
  }
}

/** Hour-bucket key for freshness scans — at most one attempt per hour. */
export function _freshKey(now = new Date()) {
  return 'fresh-' + now.toISOString().slice(0, 13);
}

function _tick() {
  const done = S.aiCopilot && S.aiCopilot.lastAutoScan && S.aiCopilot.lastAutoScan.slot;

  // 1) Fixed slots (pre-market / intraday / evening)
  const slot = _latestPassedSlot();
  if (slot && done !== slot) { _autoScan(slot); return; }

  // 2) Freshness rule — the Scan button is optional by design: whenever the
  //    app is open and the last scan is >4h old, refresh automatically.
  //    Hour-bucketed claim = max one attempt per hour, no failure loops.
  const lastTs = S.aiCopilot && S.aiCopilot.lastScan && S.aiCopilot.lastScan.ts;
  const stale = !lastTs || (Date.now() - Date.parse(lastTs)) > FRESHNESS_MS;
  if (stale) {
    const key = _freshKey();
    if (done !== key) _autoScan(key);
  }
}

/** Start the orchestrator: delayed first check (let prices/FX settle), then poll. */
export function initAIOrchestrator() {
  if (_timer) return;
  setTimeout(_tick, 45 * 1000);
  _timer = setInterval(_tick, CHECK_EVERY_MS);
}
